import React, { useEffect, useState } from "react";
import { Flame, Users, Clock, X } from "lucide-react";

/** Urgency banner — counts down to next batch start; shows seat scarcity. */
export default function UrgencyBar({ nextBatchDate, seatsLeft = 7, seatsTotal = 20 }) {
  const [remaining, setRemaining] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Default: 7 days from now if no nextBatchDate
    const target = nextBatchDate ? new Date(nextBatchDate) : new Date(Date.now() + 7 * 86400000);
    const tick = () => {
      const ms = target.getTime() - Date.now();
      if (ms <= 0) { setRemaining({ done: true }); return; }
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setRemaining({ d, h, m, s, done: false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextBatchDate]);

  if (dismissed || !remaining || remaining.done) return null;
  const pct = Math.max(5, Math.min(95, ((seatsTotal - seatsLeft) / seatsTotal) * 100));

  return (
    <div className="relative bg-gradient-to-r from-amber-500/15 via-[#FF0066]/15 to-amber-500/15 border-y border-[#FF0066]/30 px-3 md:px-4 py-2.5" data-testid="urgency-bar">
      <button onClick={() => setDismissed(true)} className="absolute top-1.5 right-2 text-white/40 hover:text-white" aria-label="Dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4 text-xs md:text-sm">
        <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center md:justify-start">
          <Flame className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-white font-semibold">Next batch starts in:</span>
          <div className="flex items-center gap-1 font-mono font-bold text-white">
            <span className="bg-[#0a0a0a] border border-white/15 rounded px-1.5 py-0.5">{String(remaining.d).padStart(2, "0")}d</span>
            <span className="bg-[#0a0a0a] border border-white/15 rounded px-1.5 py-0.5">{String(remaining.h).padStart(2, "0")}h</span>
            <span className="bg-[#0a0a0a] border border-white/15 rounded px-1.5 py-0.5">{String(remaining.m).padStart(2, "0")}m</span>
            <span className="bg-[#FF0066]/20 border border-[#FF0066]/40 text-[#FF0066] rounded px-1.5 py-0.5 animate-pulse">{String(remaining.s).padStart(2, "0")}s</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-center md:justify-end">
          <Users className="w-3.5 h-3.5 text-[#FF0066]" />
          <span className="text-white/85">Only <b className="text-[#FF0066]">{seatsLeft} of {seatsTotal}</b> seats left</span>
          <div className="w-20 md:w-28 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#FF0066] to-amber-400 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}
